/**
 * AnalysisSidebar - Accordion sidebar for selecting data series to analyze.
 *
 * Three sections:
 * - Measurements: Water test parameters (alkalinity, calcium, etc.)
 *   - Parameters with multiple sources are grouped into collapsible sub-sections
 * - Dosing: Dosing chemicals/pumps
 * - Events: Maintenance event types
 *
 * Each section header shows count of selected items when > 0.
 */
import { Accordion, Box, Text } from '@mantine/core';
import { IconFlask, IconDroplet, IconCalendarEvent } from '@tabler/icons-react';
import { AnalysisDataItem } from './AnalysisDataItem';
import { ParameterGroupItem } from './ParameterGroupItem';
import { SavedViewsSection } from './SavedViewsSection';
import { formatParameterLabel, groupParametersByName, getEventConfig } from './constants';
import type { AnalysisView } from '@/types/analysisView';

export interface AnalysisSelection {
  parameters: string[];
  dosing: string[];
  events: string[];
}

export interface AvailableData {
  parameters: string[];
  dosing: string[];
  events: string[];
}

interface AnalysisSidebarProps {
  /** What data series are available to select */
  available: AvailableData;
  /** Currently selected items */
  selected: AnalysisSelection;
  /** Callback when selection changes */
  onChange: (selection: AnalysisSelection) => void;
  /** Saved analysis views */
  savedViews?: AnalysisView[];
  /** ID of the currently active (loaded) view */
  activeViewId?: string | null;
  /** Load a saved view */
  onLoadView?: (view: AnalysisView) => void;
  /** Rename a saved view */
  onRenameView?: (viewId: string, newName: string) => void;
  /** Delete a saved view */
  onDeleteView?: (viewId: string) => void;
}

interface SectionConfig {
  key: keyof AnalysisSelection;
  label: string;
  icon: React.ReactNode;
}

const SECTIONS: SectionConfig[] = [
  { key: 'parameters', label: 'Measurements', icon: <IconFlask size={18} /> },
  { key: 'dosing', label: 'Dosing', icon: <IconDroplet size={18} /> },
  { key: 'events', label: 'Events', icon: <IconCalendarEvent size={18} /> },
];

const EVENT_ICON_SIZE = 16;

/**
 * Get the category icon for an event using the shared config.
 */
function getEventIcon(eventName: string): React.ReactNode {
  const config = getEventConfig(eventName);
  const Icon = config.icon;
  return <Icon size={EVENT_ICON_SIZE} color={config.color} />;
}

export function AnalysisSidebar({
  available,
  selected,
  onChange,
  savedViews = [],
  activeViewId = null,
  onLoadView,
  onRenameView,
  onDeleteView,
}: AnalysisSidebarProps): JSX.Element {
  /**
   * Toggle an item in a specific section
   */
  const handleToggle = (section: keyof AnalysisSelection, item: string, checked: boolean) => {
    const currentItems = selected[section];
    let newItems: string[];

    if (checked) {
      // Add item if not already present
      newItems = currentItems.includes(item) ? currentItems : [...currentItems, item];
    } else {
      // Remove item
      newItems = currentItems.filter((i) => i !== item);
    }

    onChange({
      ...selected,
      [section]: newItems,
    });
  };

  /**
   * Format section header with count
   */
  const formatSectionLabel = (label: string, count: number): string => {
    return count > 0 ? `${label} (${count})` : label;
  };

  /**
   * Get available items for a section
   */
  const getAvailableItems = (section: keyof AnalysisSelection): string[] => {
    switch (section) {
      case 'parameters':
        return available.parameters;
      case 'dosing':
        return available.dosing;
      case 'events':
        return available.events;
      default:
        return [];
    }
  };

  /**
   * Render parameters section with grouping for multi-source parameters
   */
  const renderParametersSection = () => {
    if (available.parameters.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="sm">
          No measurements available
        </Text>
      );
    }

    const groups = groupParametersByName(available.parameters);

    return (
      <Box>
        {groups.map((group) => {
          if (group.keys.length > 1) {
            // Multiple sources - render as collapsible group
            return (
              <ParameterGroupItem
                key={group.param}
                param={group.param}
                keys={group.keys}
                selectedKeys={selected.parameters}
                onToggle={(key, checked) => handleToggle('parameters', key, checked)}
              />
            );
          } else {
            // Single source - render as flat item
            const key = group.keys[0]!;
            return (
              <AnalysisDataItem
                key={key}
                itemKey={key}
                label={formatParameterLabel(key)}
                checked={selected.parameters.includes(key)}
                onChange={(checked) => handleToggle('parameters', key, checked)}
              />
            );
          }
        })}
      </Box>
    );
  };

  /**
   * Render events section with category icons
   */
  const renderEventsSection = () => {
    if (available.events.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="sm">
          No events available
        </Text>
      );
    }

    return (
      <Box>
        {available.events.map((item) => (
          <AnalysisDataItem
            key={item}
            itemKey={item}
            label={item}
            icon={getEventIcon(item)}
            checked={selected.events.includes(item)}
            onChange={(checked) => handleToggle('events', item, checked)}
          />
        ))}
      </Box>
    );
  };

  /**
   * Render a standard section (dosing)
   */
  const renderStandardSection = (section: keyof AnalysisSelection) => {
    const items = getAvailableItems(section);

    if (items.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="sm">
          No {section} available
        </Text>
      );
    }

    return (
      <Box>
        {items.map((item) => (
          <AnalysisDataItem
            key={item}
            itemKey={item}
            label={item}
            checked={selected[section].includes(item)}
            onChange={(checked) => handleToggle(section, item, checked)}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Accordion multiple defaultValue={['parameters']}>
        {SECTIONS.map((section) => {
          const selectedCount = selected[section.key].length;

          return (
            <Accordion.Item key={section.key} value={section.key}>
              <Accordion.Control icon={section.icon}>
                <Text size="sm" fw={500}>
                  {formatSectionLabel(section.label, selectedCount)}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                {section.key === 'parameters'
                  ? renderParametersSection()
                  : section.key === 'events'
                    ? renderEventsSection()
                    : renderStandardSection(section.key)}
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
      {savedViews.length > 0 && onLoadView && onRenameView && onDeleteView && (
        <SavedViewsSection
          views={savedViews}
          activeViewId={activeViewId ?? null}
          onLoad={onLoadView}
          onRename={onRenameView}
          onDelete={onDeleteView}
        />
      )}
    </>
  );
}
